import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, googleProvider } from "../firebase";
import { onAuthStateChanged, signInWithPopup, signOut, linkWithPopup, signInWithCredential, GoogleAuthProvider, reload } from "firebase/auth";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const login = async () => {
    // If the session is anonymous, link Google to preserve the UID and keep Firestore data intact.
    const current = auth.currentUser;
    if (current?.isAnonymous) {
      try {
        const cred = await linkWithPopup(current, googleProvider);
        await reload(cred.user);
        return cred;
      } catch (err) {
        // If the Google account already exists, fall back to signing into that account.
        if (err.code === "auth/credential-already-in-use") {
          const credential = GoogleAuthProvider.credentialFromError(err);
          if (credential) {
            const signed = await signInWithCredential(auth, credential);
            await reload(signed.user);
            return signed;
          }
        }
        if (err.code === "auth/account-exists-with-different-credential") {
          const credential = GoogleAuthProvider.credentialFromError(err);
          if (credential) {
            const signed = await signInWithCredential(auth, credential);
            await reload(signed.user);
            return signed;
          }
        }
        throw err;
      }
    }
    const cred = await signInWithPopup(auth, googleProvider);
    await reload(cred.user);
    return cred;
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
